import qs from "qs";
import { HttpMethods } from "../enums/HttpMethods";
import ContentFeedService from "./contentFeed.service";
import { TipFilters } from "../interfaces/TipFilters";
import { TipsResponse } from "../interfaces/TipsResponse";
import { DataWrapper } from "../interfaces/DataWrapper";
import { Tip } from "../interfaces/Tip";
import { ContentLengthOptions } from "../enums/ContentLengthOptions";
import { TIP_LENGTH_THRESHOLD } from "../components/tip-length/TipLength";

const descendingOrder = "desc";
const includeAllRelations =
  "populate=language,tags,author,variation_tips,variation_tips.language,variation_tips.user";

class TipsService {
  public static async getTipById(id: string): Promise<Tip> {
    return ContentFeedService.sendRequest<DataWrapper<Tip>>(
      HttpMethods.GET,
      `tips/${id}?${includeAllRelations}`
    )
      .then((wrappedRawContent: DataWrapper<Tip>) => {
        return wrappedRawContent.data;
      })
      .catch((error: Error) => {
        throw error;
      });
  }

  public static async getFilteredWithPagination(
    pageStart: number,
    paginationLimit: number,
    filters: TipFilters
  ): Promise<TipsResponse> {
    let query = "";

    if (filters.hasWords) {
      const words = filters.hasWords.trim().split(/[ ,]+/).filter(Boolean);

      if (words.length > 0) {
        const andConditions = words.map((word) => ({
          $or: [
            { englishTitle: { $containsi: word } },
            { translatedAnalysis: { $containsi: word } },
            { summary: { $containsi: word } },
          ],
        }));

        query +=
          "&" +
          qs.stringify({
            filters: {
              $and: andConditions,
            },
          });
      }
    }

    if (filters.tags.length > 0) {
      query +=
        "&" +
        qs.stringify({
          filters: {
            $and: filters.tags.map((tag) => {
              return {
                tags: {
                  id: { $in: tag.id },
                },
              };
            }),
          },
        });
    }

    if (filters.authors.length > 0) {
      query +=
        "&" +
        qs.stringify({
          filters: {
            $or: filters.authors.map((author) => {
              return {
                author: {
                  id: { $in: author.id },
                },
              };
            }),
          },
        });
    }

    if (filters.date) {
      query +=
        "&" +
        qs.stringify({
          filters: {
            $and: [
              {
                postCreatedAt: { $gte: filters.date.start },
              },
              {
                postCreatedAt: { $lte: filters.date.end },
              },
            ],
          },
        });
    }

    if (
      filters.contentLength &&
      filters.contentLength !== ContentLengthOptions.ALL
    ) {
      if (filters.contentLength === ContentLengthOptions.SHORT) {
        query +=
          "&" +
          qs.stringify({
            filters: {
              $and: [
                {
                  numberOfWords: {
                    $lt: TIP_LENGTH_THRESHOLD[ContentLengthOptions.MEDIUM],
                  },
                },
              ],
            },
          });
      }

      if (filters.contentLength === ContentLengthOptions.MEDIUM) {
        query +=
          "&" +
          qs.stringify({
            filters: {
              $and: [
                {
                  numberOfWords: {
                    $gte: TIP_LENGTH_THRESHOLD[ContentLengthOptions.MEDIUM],
                    $lt: TIP_LENGTH_THRESHOLD[ContentLengthOptions.LONG],
                  },
                },
              ],
            },
          });
      }

      if (filters.contentLength === ContentLengthOptions.LONG) {
        query +=
          "&" +
          qs.stringify({
            filters: {
              $and: [
                {
                  numberOfWords: {
                    $gte: TIP_LENGTH_THRESHOLD[ContentLengthOptions.LONG],
                  },
                },
              ],
            },
          });
      }
    }

    query +=
      "&" +
      qs.stringify(
        {
          sort: { postCreatedAt: descendingOrder },
          pagination: {
            page: pageStart,
            pageSize: paginationLimit,
          },
        },
        { encodeValuesOnly: true }
      );

    return ContentFeedService.sendRequest<void>(
      HttpMethods.GET,
      `tips?${query}&${includeAllRelations}`
    )
      .then((wrappedRawContents: TipsResponse) => wrappedRawContents)
      .catch((error: Error) => {
        throw error;
      });
  }
}

export default TipsService;
